package puzzlekey

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * Her unlock (T15) against one `sealed.json` and the QR secret from the Letter: the hunt's Scans, each
 * through the Radar and, when warm enough, its candidates against the Puzzle wrap (ADR-0009 §4,
 * ADR-0013 §4); or the Recovery code he sends her when she is stuck (ADR-0006). Either route ends in the
 * decrypted [Bundle] or nothing; nothing here is a check that a patched UI could skip (ADR-0001).
 */
class Unlock(private val sealed: SealedBundle, private val qrSecret: ByteArray) {
    private val radar = Radar(sealed.radar)

    /** The Radar's warmth over the Scans so far, 0…1, for the sparkles. */
    val warmth: Double get() = radar.warmth

    /** Forgets the Scans so far, e.g. when the camera starts again. */
    fun restartRadar() = radar.reset()

    /**
     * One Scan of the hunt: its [projection] warms the Radar, and its candidates run only when its own
     * score could open (not the smoothed one, which lags and would skip the first frame that opens).
     */
    suspend fun scan(projection: DoubleArray): HuntStep {
        val score = radarScore(projection, sealed.scan.mask)
        radar.next(score)
        if (!sealed.radar.worthTrying(score)) return HuntStep.Cold
        return when (val result = Envelope.openWithScan(sealed, qrSecret, projection).result) {
            OpenResult.WrongKey -> HuntStep.Missed
            else -> gift(result)
        }
    }

    /** The Recovery code as she [typed] it. */
    suspend fun recover(typed: String): RecoveryStep {
        val code = RecoveryCode.parse(typed) ?: return RecoveryStep.NotACode
        return when (val result = Envelope.openWithRecovery(sealed, qrSecret, code)) {
            OpenResult.WrongKey -> RecoveryStep.WrongCode
            else -> gift(result)
        }
    }

    /**
     * The opened [bundle] as her phone keeps it (T16): iOS can drop the tab, and the reveal must not be lost.
     * It names the seal it came from, so a re-seal (a rehearsal's dummy, then the real one) isn't stood in for.
     */
    fun keepsake(bundle: Bundle): ByteArray =
        Json.encodeToString(Keepsake.serializer(), Keepsake(sealed.iv, bundle.toBytes().decodeToString())).encodeToByteArray()

    /** The gift [kept] by [keepsake] from this seal; null when it's from another seal or isn't a keepsake. */
    fun reopen(kept: ByteArray): Bundle? =
        decodeOrNull(kept, Keepsake.serializer())?.takeIf { it.seal == sealed.iv }?.let { Bundle.fromBytes(it.bundle.encodeToByteArray()) }

    /** [seal] is the sealed Bundle's IV: random per seal. */
    @Serializable
    private class Keepsake(val seal: String, val bundle: String)

    private fun gift(result: OpenResult): Unlocked =
        (result as? OpenResult.Opened)?.let { Bundle.fromBytes(it.bundle) }?.let(::GiftOpened) ?: GiftBroken
}

/** What one Scan of the hunt came to. */
sealed interface HuntStep {
    /** The Radar said cold, so its candidates didn't run. */
    data object Cold : HuntStep

    /** Warm, but none of its candidates opened the wrap. */
    data object Missed : HuntStep
}

/** What the typed Recovery code came to. */
sealed interface RecoveryStep {
    /** Not 20 Crockford base32 symbols, so not tried. */
    data object NotACode : RecoveryStep

    /** A well-formed code that doesn't open the wrap. */
    data object WrongCode : RecoveryStep
}

/** Either route opened the Data key. */
sealed interface Unlocked : HuntStep, RecoveryStep

/** The gift: the decrypted Bundle. */
class GiftOpened(val bundle: Bundle) : Unlocked

/** The Data key opened, but the Bundle doesn't authenticate or parse: the deployed `sealed.json` is broken. */
data object GiftBroken : Unlocked
